'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import {
  ArrowLeft,
  Send,
  Loader2,
  AlertCircle,
  X,
} from 'lucide-react';
import { toast } from '@/lib/toast';
import {
  createTicket,
  getCategories,
  TicketCategory,
  TicketPriority,
  TicketSource,
  CreateTicketParams,
  getPriorityLabel,
  ApiClientError,
} from '@/services/api/tickets';

/**
 * 新建工单页面
 * 遵循飞书风格设计规范
 */
export default function NewTicketPage() {
  const router = useRouter();
  const { t } = useTranslation();

  // 表单状态
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [priority, setPriority] = useState<TicketPriority>(TicketPriority.MEDIUM);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  // 加载状态
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // 表单验证
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 加载分类
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getCategories();
        setCategories(data.filter((c) => c.isActive));
      } catch (error) {
        console.error('Failed to load categories:', error);
        toast.error(t.tickets.newTicket.loadCategoriesFailed);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
  }, [t]);

  // 验证表单
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = t.tickets.validation.titleRequired;
    } else if (title.trim().length < 5) {
      newErrors.title = t.tickets.validation.titleTooShort;
    }

    if (!description.trim()) {
      newErrors.description = t.tickets.validation.descriptionRequired;
    } else if (description.trim().length < 10) {
      newErrors.description = t.tickets.validation.descriptionTooShort;
    }

    if (!categoryId) {
      newErrors.categoryId = t.tickets.validation.categoryRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 添加标签
  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !tags.includes(tag) && tags.length < 5) {
      setTags([...tags, tag]);
      setTagInput('');
    }
  };

  // 移除标签
  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  // 提交表单
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const params: CreateTicketParams = {
        title: title.trim(),
        description: description.trim(),
        categoryId,
        priority,
        tags: tags.length > 0 ? tags : undefined,
        source: TicketSource.WEB,
      };

      const ticket = await createTicket(params);
      toast.success(t.tickets?.success?.created || '工单创建成功');
      router.push(`/tickets/${ticket.id}`);
    } catch (error) {
      if (error instanceof ApiClientError) {
        if (error.isValidationError() && error.errors) {
          const fieldErrors: Record<string, string> = {};
          error.errors.forEach((err) => {
            fieldErrors[err.field] = err.message;
          });
          setErrors(fieldErrors);
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error(t.tickets.newTicket.createFailed);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#f7f8fa' }}>
      {/* 标题栏 - 飞书风格，标准 h-12 高度 */}
      <div className="bg-white border-b" style={{ borderColor: '#e5e6eb' }}>
        <div className="px-6 h-12">
          <div className="flex items-center justify-between h-full">
            {/* 左侧：返回按钮 + 标题 */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => router.back()}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  backgroundColor: '#f7f8fa',
                  color: '#1f2329'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#eff0f2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f7f8fa';
                }}
              >
                <ArrowLeft className="w-4 h-4" />
                {t.tickets.newTicket.back}
              </button>
              <span className="text-sm font-medium" style={{ color: '#1f2329' }}>
                {t.tickets.newTicket.title}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 内容区域 - flex-1 自动占满剩余空间 */}
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-3xl mx-auto">
          {/* 表单卡片 */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6" style={{ border: '1px solid #e5e6eb' }}>
            {/* 标题 */}
            <div className="space-y-2 mb-6">
              <label className="block text-sm font-medium" style={{ color: '#1f2329' }}>
                {t.tickets.newTicket.titleLabel} <span className="text-red-500">*</span>
              </label>
              <input
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (errors.title) setErrors({ ...errors, title: '' });
                }}
                placeholder={t.tickets.newTicket.titlePlaceholder}
                maxLength={200}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all"
                style={{
                  backgroundColor: '#ffffff',
                  border: errors.title ? '1px solid #f53f3f' : '1px solid #e5e6eb',
                  color: '#1f2329'
                }}
                onFocus={(e) => {
                  if (!errors.title) {
                    e.target.style.borderColor = '#3370ff';
                    e.target.style.boxShadow = '0 0 0 2px rgba(51, 112, 255, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  if (!errors.title) {
                    e.target.style.borderColor = '#e5e6eb';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              />
              {errors.title && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.title}
                </p>
              )}
              <p className="text-xs" style={{ color: '#8f959e' }}>{title.length}/200</p>
            </div>

            {/* 分类 */}
            <div className="space-y-2 mb-6">
              <label className="block text-sm font-medium" style={{ color: '#1f2329' }}>
                {t.tickets.newTicket.categoryLabel} <span className="text-red-500">*</span>
              </label>
              <select
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  if (errors.categoryId) setErrors({ ...errors, categoryId: '' });
                  
                  // 自动设置分类的默认优先级
                  const selectedCategory = categories.find((c) => c.id === e.target.value);
                  if (selectedCategory?.defaultPriority) {
                    setPriority(selectedCategory.defaultPriority);
                  }
                }}
                disabled={loadingCategories}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all cursor-pointer"
                style={{
                  backgroundColor: '#ffffff',
                  border: errors.categoryId ? '1px solid #f53f3f' : '1px solid #e5e6eb',
                  color: '#1f2329'
                }}
                onFocus={(e) => {
                  if (!errors.categoryId) {
                    e.target.style.borderColor = '#3370ff';
                    e.target.style.boxShadow = '0 0 0 2px rgba(51, 112, 255, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  if (!errors.categoryId) {
                    e.target.style.borderColor = '#e5e6eb';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                <option value="">{t.tickets.newTicket.categoryPlaceholder}</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              {errors.categoryId && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.categoryId}
                </p>
              )}
            </div>

            {/* 优先级 */}
            <div className="space-y-2 mb-6">
              <label className="block text-sm font-medium" style={{ color: '#1f2329' }}>
                {t.tickets.newTicket.priorityLabel}
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as TicketPriority)}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all cursor-pointer"
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e6eb',
                  color: '#1f2329'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3370ff';
                  e.target.style.boxShadow = '0 0 0 2px rgba(51, 112, 255, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e6eb';
                  e.target.style.boxShadow = 'none';
                }}
              >
                {Object.values(TicketPriority).map((p) => (
                  <option key={p} value={p}>
                    {getPriorityLabel(p, t)}
                  </option>
                ))}
              </select>
            </div>

            {/* 描述 */}
            <div className="space-y-2 mb-6">
              <label className="block text-sm font-medium" style={{ color: '#1f2329' }}>
                {t.tickets.newTicket.descriptionLabel} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (errors.description) setErrors({ ...errors, description: '' });
                }}
                placeholder={t.tickets.newTicket.descriptionPlaceholder}
                rows={6}
                maxLength={5000}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all resize-none"
                style={{
                  backgroundColor: '#ffffff',
                  border: errors.description ? '1px solid #f53f3f' : '1px solid #e5e6eb',
                  color: '#1f2329'
                }}
                onFocus={(e) => {
                  if (!errors.description) {
                    e.target.style.borderColor = '#3370ff';
                    e.target.style.boxShadow = '0 0 0 2px rgba(51, 112, 255, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  if (!errors.description) {
                    e.target.style.borderColor = '#e5e6eb';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              />
              {errors.description && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.description}
                </p>
              )}
              <p className="text-xs" style={{ color: '#8f959e' }}>{description.length}/5000</p>
            </div>

            {/* 标签 */}
            <div className="space-y-2 mb-6">
              <label className="block text-sm font-medium" style={{ color: '#1f2329' }}>
                {t.tickets.newTicket.tagsLabel}
                <span className="text-xs ml-2" style={{ color: '#8f959e' }}>{t.tickets.newTicket.tagsOptional}</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  placeholder={t.tickets.newTicket.tagsPlaceholder}
                  maxLength={20}
                  disabled={tags.length >= 5}
                  className="flex-1 px-3 py-2 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e6eb',
                    color: '#1f2329'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3370ff';
                    e.target.style.boxShadow = '0 0 0 2px rgba(51, 112, 255, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e6eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  disabled={!tagInput.trim() || tags.length >= 5}
                  className="px-3 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{
                    backgroundColor: !tagInput.trim() || tags.length >= 5 ? '#f7f8fa' : '#f7f8fa',
                    color: !tagInput.trim() || tags.length >= 5 ? '#c9cdd4' : '#1f2329',
                    cursor: !tagInput.trim() || tags.length >= 5 ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (tagInput.trim() && tags.length < 5) {
                      e.currentTarget.style.backgroundColor = '#eff0f2';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f7f8fa';
                  }}
                >
                  {t.tickets.newTicket.addTag}
                </button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-1 text-sm rounded"
                      style={{
                        backgroundColor: 'rgba(51, 112, 255, 0.1)',
                        color: '#3370ff'
                      }}
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="transition-colors"
                        style={{ color: '#8f959e' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#3370ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#8f959e';
                        }}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* 提交按钮 */}
            <div className="flex items-center justify-end gap-3 pt-4" style={{ borderTop: '1px solid #e5e6eb' }}>
              <button
                type="button"
                onClick={() => router.back()}
                className="px-4 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  backgroundColor: '#f7f8fa',
                  color: '#1f2329'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#eff0f2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f7f8fa';
                }}
              >
                {t.tickets.newTicket.cancel}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  backgroundColor: loading ? '#c9cdd4' : '#3370ff',
                  color: '#ffffff',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!loading) e.currentTarget.style.backgroundColor = '#1e5eff';
                }}
                onMouseLeave={(e) => {
                  if (!loading) e.currentTarget.style.backgroundColor = '#3370ff';
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t.tickets.newTicket.submitting}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {t.tickets.newTicket.submit}
                  </>
                )}
              </button>
            </div>
          </form>

          {/* 提示信息 */}
          <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: 'rgba(51, 112, 255, 0.1)' }}>
            <h3 className="text-sm font-medium mb-2" style={{ color: '#1f2329' }}>
              {t.tickets.newTicket.tipsTitle}
            </h3>
            <ul className="text-sm space-y-1 list-disc list-inside" style={{ color: '#646a73' }}>
              <li>{t.tickets.newTicket.tip1}</li>
              <li>{t.tickets.newTicket.tip2}</li>
              <li>{t.tickets.newTicket.tip3}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
